import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Database, ShieldCheck, GitBranch, BarChart3, Activity, Target, Cpu, MessageSquare, Workflow, Bell, FileText, Lock, CheckCircle2, Circle, Sparkles, ChevronRight } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ContactModal from "@/components/ContactModal";
import { XLogo } from "@/components/Logo";
import ArchitectureDiagram from "@/components/ArchitectureDiagram";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { PLATFORM_CAPABILITIES, SECURITY_ITEMS, ROADMAP_PHASES, ENGAGEMENT_TIERS, VALUE_LEVERS, NORTHSTAR_PROFILE, MAPLETRUST_PROFILE, SYNTHETIC_DISCLAIMER, BOARD_COPILOT } from "@/data/synthetic";

const ICONS = { Database, ShieldCheck, GitBranch, BarChart3, Activity, Target, Cpu, MessageSquare, Workflow, Bell, FileText, Lock };

const SectionLabel = ({ n, children }) => (
  <div className="flex items-center gap-3 mb-4">
    <span className="section-label">§ {n}</span>
    <span className="h-px w-10 bg-cyan-400/40" />
    <span className="text-xs text-slate-400 font-mono uppercase tracking-widest">{children}</span>
  </div>
);

const Chip = ({ children, tone = "default" }) => {
  const tones = {
    default: "bg-white/5 border-white/10 text-slate-300",
    cyan: "bg-cyan-500/10 border-cyan-400/30 text-cyan-300",
    violet: "bg-violet-500/10 border-violet-400/30 text-violet-300",
    blue: "bg-blue-500/10 border-blue-400/30 text-blue-300",
  };
  return <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-mono uppercase tracking-wider border ${tones[tone]}`}>{children}</span>;
};

/* --------------- Hero --------------- */
function Hero({ onContact }) {
  return (
    <section className="relative pt-32 pb-24 overflow-hidden noise" data-testid="hero-section">
      <div className="absolute inset-0 bg-grid opacity-[0.35] pointer-events-none" />
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full" style={{ background: "radial-gradient(closest-side, rgba(0,82,255,0.28), transparent 70%)" }} />
      </div>
      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-2 mb-6">
              <Chip tone="cyan">US · CA · ENTERPRISE</Chip>
              <Chip tone="violet">FMCG · BANKING</Chip>
              <Chip>V4.2 · Q1 2026</Chip>
            </div>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tighter font-medium text-white">
              Transforming <span className="text-gradient-cyan">complexity</span><br />into clarity.
            </h1>
            <p className="mt-6 text-lg text-slate-300 max-w-xl leading-relaxed">
              Xyloctra converts fragmented enterprise data into intelligence, recommended actions and measured outcomes — for commercial and boardroom decisions across the United States and Canada.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/demo/commercial" className="px-5 py-3 rounded-md text-sm font-medium btn-primary inline-flex items-center gap-2" data-testid="hero-cta-demo">
                Explore the Demo <ArrowRight size={16} />
              </Link>
              <a href="#cases" className="px-5 py-3 rounded-md text-sm btn-ghost inline-flex items-center gap-2" data-testid="hero-cta-cases">
                Review Case Studies <ArrowUpRight size={16} />
              </a>
              <button onClick={onContact} className="text-sm text-slate-300 hover:text-white px-3 py-3" data-testid="hero-cta-contact">Talk to Xyloctra →</button>
            </div>
            <div className="mt-10 grid grid-cols-3 gap-6 max-w-lg">
              <div>
                <div className="text-2xl font-heading font-semibold text-white">24+</div>
                <div className="text-xs text-slate-400 mt-1">Enterprise sources unified</div>
              </div>
              <div>
                <div className="text-2xl font-heading font-semibold text-white">68<span className="text-cyan-400">%</span></div>
                <div className="text-xs text-slate-400 mt-1">Reporting time reduction</div>
              </div>
              <div>
                <div className="text-2xl font-heading font-semibold text-white">US · CA</div>
                <div className="text-xs text-slate-400 mt-1">Multi-region deployments</div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-5">
            <HeroVisual />
          </div>
        </div>
      </div>
    </section>
  );
}

function HeroVisual() {
  return (
    <div className="relative glass rounded-2xl p-6 shadow-2xl" data-testid="hero-visual">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <XLogo size={22} />
          <div className="text-xs font-mono text-slate-300">XYLOCTRA · LIVE COCKPIT</div>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 pulse-ring" />
          <span className="tech-label">SYNC · ACTIVE</span>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3">
        {[
          { l: "Net Sales", v: "$284.6M", d: "+4.2%" },
          { l: "At Risk", v: "$18.9M", d: "+2.1M" },
          { l: "Opportunity", v: "$3.8M", d: "1,840" },
        ].map((k) => (
          <div key={k.l} className="rounded-lg bg-white/[0.02] border border-white/5 p-3">
            <div className="text-[10px] tech-label">{k.l}</div>
            <div className="text-lg font-heading font-semibold text-white mt-1">{k.v}</div>
            <div className="text-[10px] text-cyan-300 mt-0.5 font-mono">{k.d}</div>
          </div>
        ))}
      </div>
      <div className="mt-4 rounded-lg bg-black/40 border border-white/5 p-4">
        <div className="tech-label mb-2">DATA → DECISION FLOW</div>
        <svg viewBox="0 0 400 130" className="w-full h-32">
          <defs>
            <linearGradient id="hg" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#00E5FF" />
              <stop offset="100%" stopColor="#0052FF" />
            </linearGradient>
          </defs>
          {["Ingest", "Curate", "Detect", "Score", "Act"].map((s, i) => (
            <g key={s} transform={`translate(${20 + i * 90}, 55)`}>
              <circle r="14" fill="rgba(0,229,255,0.08)" stroke="url(#hg)" strokeWidth="1.5" />
              <text y="4" textAnchor="middle" className="fill-white" style={{ fontSize: 9, fontFamily: "JetBrains Mono" }}>{s}</text>
            </g>
          ))}
          <line x1="34" y1="55" x2="380" y2="55" stroke="url(#hg)" strokeWidth="1" className="animate-dash" />
        </svg>
      </div>
      <div className="mt-4 rounded-lg border border-white/5 p-3 flex items-start gap-3">
        <div className="w-8 h-8 rounded-md bg-cyan-500/10 border border-cyan-400/30 flex items-center justify-center flex-shrink-0">
          <Sparkles size={14} className="text-cyan-300" />
        </div>
        <div>
          <div className="text-[11px] text-slate-400 font-mono">AI COPILOT · SUGGESTION</div>
          <div className="text-sm text-slate-100 mt-0.5">Recover $1.4M in Midwest by reassigning 12 high-value outlets to Distributor A.</div>
        </div>
      </div>
    </div>
  );
}

/* --------------- Problem section --------------- */
function ProblemFlow() {
  const steps = ["Data", "Reports", "Insights", "Decisions", "Actions", "Measured Outcomes"];
  const questions = ["What changed", "Why it changed", "Where value is lost", "Which action to take", "Who should own it", "Whether it created impact"];
  return (
    <section className="py-24 relative" id="problem" data-testid="problem-section">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <SectionLabel n="02">The enterprise problem</SectionLabel>
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-6">
            <h2 className="font-heading text-4xl sm:text-5xl tracking-tight text-white leading-[1.05] font-medium">
              You have ERP, CRM, BI, DMS, risk, audit and transaction systems.<br />
              <span className="text-slate-400">Leaders still can&apos;t answer six questions.</span>
            </h2>
            <div className="mt-8 space-y-3">
              {questions.map((q, i) => (
                <div key={q} className="flex items-center gap-3 border-l-2 border-cyan-400/40 pl-4 py-1">
                  <span className="font-mono text-xs text-cyan-400 w-6">Q{i + 1}</span>
                  <span className="text-slate-200">{q}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-6">
            <div className="glass rounded-2xl p-6">
              <div className="tech-label mb-4">FROM DATA TO MEASURED OUTCOMES</div>
              <div className="space-y-2">
                {steps.map((s, i) => (
                  <div key={s} className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-md bg-white/5 border border-white/10 flex items-center justify-center font-mono text-xs text-cyan-300">{i + 1}</div>
                    <div className="flex-1 h-11 rounded-md border border-white/10 flex items-center justify-between px-4"
                      style={{ background: `linear-gradient(90deg, rgba(0,229,255,${0.03 + i * 0.02}), rgba(0,82,255,${0.03 + i * 0.02}))` }}>
                      <span className="text-slate-100">{s}</span>
                      {i < steps.length - 1 && <ChevronRight size={16} className="text-slate-500" />}
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-5 text-xs text-slate-400 font-mono">
                Reports alone don&apos;t drive outcomes. Xyloctra closes the loop.
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* --------------- Platform --------------- */
function Platform() {
  return (
    <section className="py-24 relative" id="platform" data-testid="platform-section">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <SectionLabel n="03">Xyloctra platform</SectionLabel>
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10">
          <h2 className="font-heading text-4xl sm:text-5xl tracking-tight text-white font-medium max-w-3xl">
            One reusable platform. Twelve capabilities. Built for enterprise scale.
          </h2>
          <p className="text-slate-400 max-w-md">The core is shared across products. Business outcomes are configured per domain, per team, per market.</p>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {PLATFORM_CAPABILITIES.map((c) => {
            const I = ICONS[c.icon] || Database;
            return (
              <div key={c.code} className="glass glass-hover rounded-xl p-5 group transition-colors" data-testid={`capability-${c.code}`}>
                <div className="flex items-start justify-between">
                  <div className="w-9 h-9 rounded-md bg-cyan-500/10 border border-cyan-400/20 flex items-center justify-center">
                    <I size={16} className="text-cyan-300" />
                  </div>
                  <span className="font-mono text-[10px] text-slate-500">{c.code}</span>
                </div>
                <div className="font-heading text-white mt-4 tracking-tight">{c.title}</div>
                <div className="text-xs text-slate-400 mt-2 leading-relaxed">{c.desc}</div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* --------------- Product Portfolio --------------- */
function Portfolio({ onContact }) {
  const products = [
    { key: "ce", name: "Xyloctra Commercial Excellence", tag: "FMCG / CPG", desc: "For VP Sales, Commercial Excellence, RGM, Customer Development, territory and distributor management teams.", caps: ["Revenue leakage detection", "Outlet opportunity prioritization", "Distributor health intelligence", "Promotion effectiveness", "Assortment opportunity detection", "Sales anomaly explanation", "Daily territory brief", "Next-best-action recommendations", "Closed-loop action tracking"], to: "/demo/commercial", cta: "Open Commercial Demo", accent: "cyan" },
    { key: "bi", name: "Xyloctra Board Intelligence", tag: "Banking / Governance", desc: "For CEO, CFO, CRO, Compliance Head, Internal Audit Head, Company Secretary, board and governance teams.", caps: ["Board pack automation", "Regulatory intelligence", "Audit intelligence", "Governance action tracking", "Risk commentary", "Executive summaries", "Source-grounded copilot", "Approval and audit workflows"], to: "/demo/board", cta: "Open Board Demo", accent: "violet" },
  ];
  return (
    <section className="py-24 relative" id="products" data-testid="products-section">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <SectionLabel n="04">Product portfolio</SectionLabel>
        <h2 className="font-heading text-4xl sm:text-5xl tracking-tight text-white font-medium max-w-3xl">
          Two products. One platform. Purpose-built for the teams that make decisions.
        </h2>
        <div className="grid lg:grid-cols-2 gap-6 mt-10">
          {products.map((p) => (
            <div key={p.key} className={`glass rounded-2xl p-8 relative overflow-hidden`} data-testid={`product-${p.key}`}>
              <div className={`absolute -top-32 -right-32 w-72 h-72 rounded-full ${p.accent === "cyan" ? "bg-cyan-500/10" : "bg-violet-500/10"} blur-3xl`} />
              <div className="relative">
                <Chip tone={p.accent}>{p.tag}</Chip>
                <h3 className="mt-4 font-heading text-2xl sm:text-3xl tracking-tight text-white font-medium">{p.name}</h3>
                <p className="mt-3 text-slate-300 max-w-md">{p.desc}</p>
                <ul className="mt-6 grid grid-cols-1 gap-2">
                  {p.caps.map((c) => (
                    <li key={c} className="flex items-center gap-2 text-sm text-slate-200">
                      <CheckCircle2 size={14} className={p.accent === "cyan" ? "text-cyan-400" : "text-violet-400"} />
                      {c}
                    </li>
                  ))}
                </ul>
                <div className="mt-8 flex flex-wrap items-center gap-3">
                  <Link to={p.to} className="px-5 py-3 rounded-md text-sm btn-primary inline-flex items-center gap-2" data-testid={`product-${p.key}-cta`}>
                    {p.cta} <ArrowRight size={14} />
                  </Link>
                  <button onClick={onContact} className="px-5 py-3 rounded-md text-sm btn-ghost" data-testid={`product-${p.key}-workshop`}>Schedule Workshop</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* --------------- Case Studies --------------- */
function CaseStudies() {
  return (
    <section className="py-24 relative" id="cases" data-testid="cases-section">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <SectionLabel n="05">Illustrative case studies</SectionLabel>
        <h2 className="font-heading text-4xl sm:text-5xl tracking-tight text-white font-medium max-w-3xl">
          Two detailed walk-throughs. Fictional companies. Realistic scenarios.
        </h2>
        <p className="mt-4 text-slate-400 max-w-2xl">{SYNTHETIC_DISCLAIMER}</p>

        {/* FMCG */}
        <div className="mt-12 glass rounded-2xl overflow-hidden" data-testid="case-fmcg">
          <div className="grid lg:grid-cols-5">
            <div className="lg:col-span-2 relative min-h-[280px]">
              <img src="https://images.unsplash.com/photo-1553413077-190dd305871c?crop=entropy&cs=srgb&fm=jpg&q=85" alt="Warehouse" className="absolute inset-0 w-full h-full object-cover opacity-45" />
              <div className="absolute inset-0 bg-gradient-to-r from-[#0B1021] via-[#0B1021]/60 to-transparent" />
              <div className="relative p-8">
                <Chip tone="cyan">FMCG · CPG</Chip>
                <div className="font-heading text-2xl sm:text-3xl text-white mt-4 tracking-tight">{NORTHSTAR_PROFILE.name}</div>
                <ul className="mt-4 space-y-1.5 text-sm text-slate-300">
                  {NORTHSTAR_PROFILE.bullets.map((b) => <li key={b} className="flex gap-2"><span className="text-cyan-400">·</span>{b}</li>)}
                </ul>
              </div>
            </div>
            <div className="lg:col-span-3 p-8">
              <Accordion type="multiple" defaultValue={["problem", "output"]}>
                <AccordionItem value="problem" className="border-white/5">
                  <AccordionTrigger className="text-white hover:no-underline" data-testid="case-fmcg-problem">Problem</AccordionTrigger>
                  <AccordionContent>
                    <ul className="text-sm text-slate-300 space-y-1.5">
                      <li>Sales and distributor information spread across multiple systems</li>
                      <li>Managers receive reports but no prioritized actions</li>
                      <li>Inactive outlets are discovered late</li>
                      <li>Assortment opportunities are missed</li>
                      <li>Distributor inventory issues create lost sales</li>
                      <li>Promotion ROI is difficult to explain</li>
                      <li>Commercial review is largely manual</li>
                    </ul>
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="sources" className="border-white/5">
                  <AccordionTrigger className="text-white hover:no-underline">Source data</AccordionTrigger>
                  <AccordionContent>
                    <div className="flex flex-wrap gap-2">{NORTHSTAR_PROFILE.sources.map((s) => <Chip key={s}>{s}</Chip>)}</div>
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="approach" className="border-white/5">
                  <AccordionTrigger className="text-white hover:no-underline">Approach</AccordionTrigger>
                  <AccordionContent>
                    <ol className="text-sm text-slate-300 space-y-1.5 list-decimal ml-5">
                      {NORTHSTAR_PROFILE.approach.map((a) => <li key={a}>{a}</li>)}
                    </ol>
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="signals" className="border-white/5">
                  <AccordionTrigger className="text-white hover:no-underline">Example signals</AccordionTrigger>
                  <AccordionContent>
                    <div className="flex flex-wrap gap-2">{NORTHSTAR_PROFILE.signals.map((s) => <Chip key={s} tone="cyan">{s}</Chip>)}</div>
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="output" className="border-white/5">
                  <AccordionTrigger className="text-white hover:no-underline">Example output</AccordionTrigger>
                  <AccordionContent>
                    <div className="grid grid-cols-2 gap-3">
                      {NORTHSTAR_PROFILE.outputs.map((o) => (
                        <div key={o.k} className="rounded-lg bg-white/[0.02] border border-white/5 p-3">
                          <div className="tech-label">{o.k}</div>
                          <div className="text-lg font-heading text-white mt-1">{o.v}</div>
                        </div>
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="eval" className="border-white/5">
                  <AccordionTrigger className="text-white hover:no-underline">Evaluation design</AccordionTrigger>
                  <AccordionContent>
                    <ul className="text-sm text-slate-300 space-y-1.5">
                      {NORTHSTAR_PROFILE.evaluation.map((e) => <li key={e}>· {e}</li>)}
                    </ul>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          </div>
        </div>

        {/* Banking */}
        <div className="mt-8 glass rounded-2xl overflow-hidden" data-testid="case-banking">
          <div className="grid lg:grid-cols-5">
            <div className="lg:col-span-2 relative min-h-[280px]">
              <img src="https://images.unsplash.com/photo-1758691737182-d42aefd6dee8?crop=entropy&cs=srgb&fm=jpg&q=85" alt="Boardroom" className="absolute inset-0 w-full h-full object-cover opacity-45" />
              <div className="absolute inset-0 bg-gradient-to-r from-[#0B1021] via-[#0B1021]/60 to-transparent" />
              <div className="relative p-8">
                <Chip tone="violet">BANKING · GOVERNANCE</Chip>
                <div className="font-heading text-2xl sm:text-3xl text-white mt-4 tracking-tight">{MAPLETRUST_PROFILE.name}</div>
                <ul className="mt-4 space-y-1.5 text-sm text-slate-300">
                  {MAPLETRUST_PROFILE.bullets.map((b) => <li key={b} className="flex gap-2"><span className="text-violet-400">·</span>{b}</li>)}
                </ul>
              </div>
            </div>
            <div className="lg:col-span-3 p-8">
              <Accordion type="multiple" defaultValue={["problem", "query"]}>
                <AccordionItem value="problem" className="border-white/5">
                  <AccordionTrigger className="text-white hover:no-underline">Problem</AccordionTrigger>
                  <AccordionContent>
                    <ul className="text-sm text-slate-300 space-y-1.5">{MAPLETRUST_PROFILE.problem.map((p) => <li key={p}>· {p}</li>)}</ul>
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="modules" className="border-white/5">
                  <AccordionTrigger className="text-white hover:no-underline">Solution modules</AccordionTrigger>
                  <AccordionContent>
                    <div className="flex flex-wrap gap-2">{MAPLETRUST_PROFILE.modules.map((m) => <Chip key={m} tone="violet">{m}</Chip>)}</div>
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="query" className="border-white/5">
                  <AccordionTrigger className="text-white hover:no-underline">Executive query &amp; source-grounded answer</AccordionTrigger>
                  <AccordionContent>
                    <div className="rounded-md bg-black/40 border border-white/5 p-4 text-sm text-slate-300 italic">&quot;{BOARD_COPILOT.q}&quot;</div>
                    <div className="mt-3 overflow-x-auto rounded-md border border-white/5">
                      <table className="w-full text-xs">
                        <thead className="bg-white/5">
                          <tr className="text-left text-slate-400">
                            {["Obligation", "Regulator", "Source", "Function", "Risk", "Owner", "Due", "Status", "Escalation"].map((h) => <th key={h} className="px-3 py-2 font-mono uppercase tracking-wider text-[10px]">{h}</th>)}
                          </tr>
                        </thead>
                        <tbody>
                          {BOARD_COPILOT.answer.map((r) => (
                            <tr key={r.obligation} className="border-t border-white/5 hover:bg-white/[0.02]">
                              <td className="px-3 py-2 text-slate-200">{r.obligation}</td>
                              <td className="px-3 py-2 text-slate-300">{r.regulator}</td>
                              <td className="px-3 py-2 text-cyan-300 font-mono">{r.source}</td>
                              <td className="px-3 py-2 text-slate-300">{r.func}</td>
                              <td className="px-3 py-2"><span className={`text-[10px] font-mono ${r.risk === "High" ? "text-rose-300" : "text-amber-300"}`}>{r.risk}</span></td>
                              <td className="px-3 py-2 text-slate-300">{r.owner}</td>
                              <td className="px-3 py-2 text-slate-300 font-mono">{r.due}</td>
                              <td className="px-3 py-2 text-slate-300">{r.status}</td>
                              <td className="px-3 py-2 text-violet-300">{r.escalate}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* --------------- Architecture --------------- */
function Architecture() {
  return (
    <section className="py-24 relative" id="architecture" data-testid="architecture-section">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <SectionLabel n="09">Technical architecture</SectionLabel>
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4 mb-8">
          <h2 className="font-heading text-4xl sm:text-5xl tracking-tight text-white font-medium max-w-3xl">Modular, AWS-native, built for multi-tenant enterprise scale.</h2>
          <div className="text-xs text-slate-400 font-mono">ENV: PROD · REGION: US-EAST-1 / CA-CENTRAL-1</div>
        </div>
        <ArchitectureDiagram />
      </div>
    </section>
  );
}

/* --------------- Security --------------- */
function Security() {
  return (
    <section className="py-24 relative" data-testid="security-section">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <SectionLabel n="10">Security &amp; governance</SectionLabel>
        <h2 className="font-heading text-4xl sm:text-5xl tracking-tight text-white font-medium max-w-3xl">Enterprise-grade by default. Auditable by design.</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mt-10">
          {SECURITY_ITEMS.map((s) => (
            <div key={s} className="glass rounded-lg p-4 flex items-start gap-2">
              <Lock size={14} className="text-cyan-300 mt-0.5" />
              <div className="text-sm text-slate-200">{s}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* --------------- Roadmap --------------- */
function Roadmap() {
  return (
    <section className="py-24 relative" data-testid="roadmap-section">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <SectionLabel n="11">Implementation roadmap</SectionLabel>
        <h2 className="font-heading text-4xl sm:text-5xl tracking-tight text-white font-medium max-w-3xl">18 weeks from discovery to measured benefit.</h2>
        <div className="mt-10 relative">
          <div className="absolute left-4 top-0 bottom-0 w-px bg-gradient-to-b from-cyan-400/60 via-blue-500/40 to-violet-500/30 hidden md:block" />
          <div className="space-y-6">
            {ROADMAP_PHASES.map((p, i) => (
              <div key={p.weeks} className="md:pl-14 relative" data-testid={`roadmap-${i}`}>
                <div className="hidden md:flex absolute left-0 top-4 w-8 h-8 rounded-full bg-[#0B1021] border border-cyan-400/60 items-center justify-center">
                  <span className="w-2 h-2 rounded-full bg-cyan-400 pulse-ring" />
                </div>
                <div className="glass rounded-xl p-6">
                  <div className="flex flex-wrap items-baseline gap-3">
                    <span className="tech-label">{p.weeks}</span>
                    <h3 className="font-heading text-xl text-white tracking-tight">{p.title}</h3>
                  </div>
                  <div className="grid md:grid-cols-3 gap-6 mt-4">
                    <div>
                      <div className="text-[11px] tech-label mb-2">DELIVERABLES</div>
                      <ul className="text-sm text-slate-300 space-y-1">{p.items.map((x) => <li key={x} className="flex gap-2"><Circle size={6} className="mt-2 text-cyan-400 fill-cyan-400" />{x}</li>)}</ul>
                    </div>
                    <div>
                      <div className="text-[11px] tech-label mb-2">CUSTOMER RESPONSIBILITIES</div>
                      <ul className="text-sm text-slate-300 space-y-1">{p.us.map((x) => <li key={x}>· {x}</li>)}</ul>
                    </div>
                    <div>
                      <div className="text-[11px] tech-label mb-2">XYLOCTRA RESPONSIBILITIES</div>
                      <ul className="text-sm text-slate-300 space-y-1">{p.we.map((x) => <li key={x}>· {x}</li>)}</ul>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* --------------- Engagement --------------- */
function Engagement({ onContact }) {
  return (
    <section className="py-24 relative" id="engagement" data-testid="engagement-section">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <SectionLabel n="12">Engagement model</SectionLabel>
        <h2 className="font-heading text-4xl sm:text-5xl tracking-tight text-white font-medium max-w-3xl">Start focused. Prove value. Scale confidently.</h2>
        <div className="grid lg:grid-cols-3 gap-5 mt-10">
          {ENGAGEMENT_TIERS.map((t, i) => (
            <div key={t.name} className={`glass rounded-2xl p-8 relative ${i === 1 ? "border-cyan-400/30" : ""}`} data-testid={`tier-${i}`}>
              {i === 1 && <div className="absolute -top-3 left-6 px-2.5 py-0.5 rounded-full text-[10px] font-mono tracking-wider bg-cyan-400 text-black">RECOMMENDED · START HERE</div>}
              <Chip tone={i === 0 ? "cyan" : i === 1 ? "blue" : "violet"}>{t.tag}</Chip>
              <h3 className="mt-4 font-heading text-2xl text-white tracking-tight">{t.name}</h3>
              <p className="text-sm text-slate-400 mt-2">{t.desc}</p>
              <ul className="mt-6 space-y-2">
                {t.items.map((x) => (
                  <li key={x} className="flex items-start gap-2 text-sm text-slate-200"><CheckCircle2 size={14} className="text-cyan-400 mt-0.5" />{x}</li>
                ))}
              </ul>
              <button onClick={onContact} className="mt-6 w-full px-4 py-3 rounded-md text-sm btn-primary" data-testid={`tier-${i}-cta`}>Discuss this engagement</button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* --------------- Value --------------- */
function Value() {
  return (
    <section className="py-24 relative" data-testid="value-section">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <SectionLabel n="13">Commercial value</SectionLabel>
        <h2 className="font-heading text-4xl sm:text-5xl tracking-tight text-white font-medium max-w-3xl">Eight measurable levers. Zero unrealistic guarantees.</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
          {VALUE_LEVERS.map((v) => (
            <div key={v.k} className="glass rounded-xl p-5">
              <div className="font-heading text-white tracking-tight">{v.k}</div>
              <div className="text-xs text-slate-400 mt-2 leading-relaxed">{v.d}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* --------------- About --------------- */
function About() {
  return (
    <section className="py-24 relative" data-testid="about-section">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <SectionLabel n="14">About Xyloctra</SectionLabel>
        <div className="grid lg:grid-cols-3 gap-5">
          {[
            { l: "MISSION", t: "Transform complexity into clarity." },
            { l: "VISION", t: "Enable enterprises to make faster, smarter and more confident decisions." },
            { l: "BRAND PROMISE", t: "From data to intelligence. From intelligence to action." },
          ].map((x) => (
            <div key={x.l} className="glass rounded-2xl p-8">
              <div className="tech-label">{x.l}</div>
              <div className="font-heading text-2xl sm:text-3xl text-white mt-3 tracking-tight leading-snug">{x.t}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* --------------- Closing --------------- */
function Closing({ onContact }) {
  return (
    <section className="py-24 relative noise" data-testid="closing-section">
      <div className="absolute inset-0 pointer-events-none opacity-70">
        <div className="absolute inset-0 bg-grid-fine opacity-30" />
      </div>
      <div className="relative max-w-[1200px] mx-auto px-6 lg:px-10 text-center">
        <SectionLabel n="15">Closing</SectionLabel>
        <h2 className="font-heading text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tighter text-white font-medium">
          See what matters. Understand why.<br />
          <span className="text-gradient-cyan">Act with confidence.</span>
        </h2>
        <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
          <button onClick={onContact} className="px-5 py-3 rounded-md text-sm btn-primary" data-testid="closing-diagnostic">Start a Revenue Leakage Diagnostic</button>
          <button onClick={onContact} className="px-5 py-3 rounded-md text-sm btn-ghost" data-testid="closing-workshop">Schedule a Commercial Excellence Workshop</button>
          <Link to="/demo/board" className="px-5 py-3 rounded-md text-sm btn-ghost inline-flex items-center gap-2" data-testid="closing-board">Explore Board Intelligence <ArrowRight size={14} /></Link>
          <button onClick={onContact} className="px-5 py-3 rounded-md text-sm btn-ghost" data-testid="closing-contact">Contact Xyloctra</button>
        </div>
      </div>
    </section>
  );
}

export default function Marketing() {
  const [contactOpen, setContactOpen] = useState(false);
  const openContact = () => setContactOpen(true);
  return (
    <div className="min-h-screen text-slate-200 relative">
      <Navbar />
      <Hero onContact={openContact} />
      <ProblemFlow />
      <Platform />
      <Portfolio onContact={openContact} />
      <CaseStudies />
      <Architecture />
      <Security />
      <Roadmap />
      <Engagement onContact={openContact} />
      <Value />
      <About />
      <Closing onContact={openContact} />
      <Footer />
      <ContactModal open={contactOpen} onOpenChange={setContactOpen} />
    </div>
  );
}
